import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Slider,
  Checkbox,
  FormControlLabel,
  Box,
  Alert,
  Radio,
  RadioGroup,
} from "@mui/material";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import LoadingButton from "@mui/lab/LoadingButton";
import { useApi } from "../../utils/api";
import { useUserInfo } from "../../utils/user";
import { calculateTotalWithPaymentsPro } from "../../utils/slotPricing";

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: "#32325d",
      fontFamily: "Roboto Mono",
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": { color: "#aab7c4" },
    },
    invalid: { color: "#fa755a", iconColor: "#fa755a" },
  },
};

function normalizeStripeError(err) {
  if (!err) return "Payment failed.";
  if (typeof err === "string") return err;
  return err.message || "Payment failed.";
}

const CoachSubscriptionModal = ({
  open,
  onClose,
  onSuccess,
  existingStripeCustomerId,
  submitLabel = "Subscribe & Add Client",
}) => {
  const { id: userId, name, email } = useUserInfo();
  const {
    createCoachSubscription,
    createPlatformCustomer,
    createSetupIntent,
    getPaymentMethods,
    setDefaultPaymentMethod,
  } = useApi();
  const stripe = useStripe();
  const elements = useElements();

  const [slots, setSlots] = useState(10);
  const [paymentsPro, setPaymentsPro] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState(null);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [useNewPaymentMethod, setUseNewPaymentMethod] = useState(true);

  const { breakdown, total, paymentsProCost } = calculateTotalWithPaymentsPro(
    slots,
    paymentsPro
  );

  useEffect(() => {
    async function loadPaymentMethods() {
      if (!open || !existingStripeCustomerId) {
        setPaymentMethods([]);
        setDefaultPaymentMethodId(null);
        setSelectedPaymentMethodId(null);
        setUseNewPaymentMethod(true);
        return;
      }

      setLoadingPaymentMethods(true);
      try {
        const pmResponse = await getPaymentMethods(existingStripeCustomerId);
        const methods = pmResponse?.paymentMethods || [];
        const defaultId = pmResponse?.defaultPaymentMethodId || methods[0]?.id || null;
        setPaymentMethods(methods);
        setDefaultPaymentMethodId(defaultId);
        if (methods.length > 0) {
          setSelectedPaymentMethodId(defaultId || methods[0].id);
          setUseNewPaymentMethod(false);
        } else {
          setSelectedPaymentMethodId(null);
          setUseNewPaymentMethod(true);
        }
      } catch (err) {
        setError(err?.message || "Failed to load saved payment methods.");
      } finally {
        setLoadingPaymentMethods(false);
      }
    }

    loadPaymentMethods();
  }, [open, existingStripeCustomerId, getPaymentMethods]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!stripe || !elements) return;

    setLoading(true);

    try {
      let stripeCustomerId = existingStripeCustomerId;

      if (!stripeCustomerId) {
        const createResp = await createPlatformCustomer({
          email,
          name,
          userId,
        });
        stripeCustomerId = createResp?.stripeCustomerId;
        if (!stripeCustomerId) {
          throw new Error("Failed to create customer. Please try again.");
        }
        localStorage.removeItem("userInfo");
      }

      let paymentMethodId = selectedPaymentMethodId;
      if (!paymentMethodId || useNewPaymentMethod) {
        const card = elements.getElement(CardElement);
        if (!card) throw new Error("Card form not ready.");

        const { client_secret: setupSecret } = await createSetupIntent({
          stripeCustomerId,
        });
        if (!setupSecret) throw new Error("Failed to create setup intent.");

        const { error: setupError, setupIntent } = await stripe.confirmCardSetup(
          setupSecret,
          {
            payment_method: {
              card,
              billing_details: { email },
            },
          }
        );
        if (setupError) throw new Error(normalizeStripeError(setupError));
        paymentMethodId = setupIntent?.payment_method;
        if (!paymentMethodId)
          throw new Error("Card setup did not return a payment method.");

        await setDefaultPaymentMethod({
          paymentMethodId,
          stripeCustomerId,
        });
      }

      const subResp = await createCoachSubscription({
        slots,
        paymentsPro,
      });

      if (!subResp?.ok) {
        throw new Error(subResp?.error || "Failed to create subscription.");
      }

      const clientSecret = subResp?.paymentIntentClientSecret;
      if (clientSecret) {
        const { error: piError } = await stripe.confirmCardPayment(
          clientSecret,
          { payment_method: paymentMethodId }
        );
        if (piError) throw new Error(normalizeStripeError(piError));
      }

      onSuccess?.();
      onClose?.();
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !loading && onClose?.()}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Select your number of client slots</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose your number of client slots. You can change this later.
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2">
              Client slots: <strong>{slots}</strong>
            </Typography>
            <Slider
              value={slots}
              onChange={(_, v) => setSlots(v)}
              min={1}
              max={100}
              valueLabelDisplay="auto"
              marks={[
                { value: 10, label: "10" },
                { value: 30, label: "30" },
                { value: 60, label: "60" },
                { value: 100, label: "100" },
              ]}
            />
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={paymentsPro}
                onChange={(e) => setPaymentsPro(e.target.checked)}
              />
            }
            label="Stripe / Payments Pro Add-On ($10/month)"
          />
          <Box sx={{ bgcolor: "grey.50", p: 2, borderRadius: 1, mt: 2 }}>
            {breakdown.map((row) => (
              <Typography key={row.range} variant="body2">
                {row.slots} slots in {row.range} × ${row.pricePerSlot} = $
                {row.cost}
              </Typography>
            ))}
            {paymentsProCost > 0 && (
              <Typography variant="body2">
                Payments Pro: ${paymentsProCost}
              </Typography>
            )}
            <Typography variant="h6" sx={{ mt: 1 }}>
              Total: ${total}/month
            </Typography>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Payment method
            </Typography>
            {loadingPaymentMethods ? (
              <Typography variant="body2" color="text.secondary">
                Loading saved payment methods...
              </Typography>
            ) : paymentMethods.length > 0 ? (
              <>
                <RadioGroup
                  value={useNewPaymentMethod ? "new" : selectedPaymentMethodId || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "new") {
                      setUseNewPaymentMethod(true);
                      return;
                    }
                    setUseNewPaymentMethod(false);
                    setSelectedPaymentMethodId(value);
                  }}
                >
                  {paymentMethods.map((pm) => (
                    <FormControlLabel
                      key={pm.id}
                      value={pm.id}
                      control={<Radio />}
                      label={`${pm.card?.brand?.toUpperCase() || "CARD"} •••• ${
                        pm.card?.last4 || "****"
                      } Exp: ${pm.card?.exp_month || "**"}/${
                        pm.card?.exp_year || "**"
                      }${pm.id === defaultPaymentMethodId ? " (Default)" : ""}`}
                    />
                  ))}
                  <FormControlLabel
                    value="new"
                    control={<Radio />}
                    label="Use a new card"
                  />
                </RadioGroup>
                {useNewPaymentMethod && (
                  <Box sx={{ mt: 1 }}>
                    <CardElement options={CARD_ELEMENT_OPTIONS} />
                  </Box>
                )}
              </>
            ) : (
              <CardElement options={CARD_ELEMENT_OPTIONS} />
            )}
          </Box>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={loading}
            disabled={!stripe || loading}
          >
            {submitLabel}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CoachSubscriptionModal;
